import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Target, BarChart, Zap } from "lucide-react";

export default function Contact() {
    const [formState, setFormState] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState("");
    const formRef = useRef(null);
    const honeypotRef = useRef(null);
    const honeypotInputRef = useRef(null);
    const formStartTimeRef = useRef(0);
    const fieldRefs = useRef({
        name: null,
        email: null,
        subject: null,
        message: null,
    });

    function generateRandomString(length) {
        const characters = 'abcdefghijklmnopqrstuvwxyz';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    }

    useEffect(() => {
        if (formRef.current) {
            formStartTimeRef.current = new Date().getTime();

            const part1 = 'aHR0cHM6Ly9jb250YWN0LWZvcm0tc2VydmljZS0xMjI2NzI1NTQwLnVzLWNlbnRyYWwxLnJ1bi5hcHAv';
            const part2 = 'YXBpL2NvbnRhY3Q=';
            const endpoint = atob(part1) + atob(part2);
            formRef.current.action = endpoint;

            if (honeypotRef.current && honeypotInputRef.current) {
                const randomHoneypotName = generateRandomString(10);
                const honeypotLabel = honeypotRef.current.querySelector('label');

                if (honeypotLabel) {
                    honeypotLabel.setAttribute('for', randomHoneypotName);
                }

                honeypotInputRef.current.id = randomHoneypotName;
                honeypotInputRef.current.name = randomHoneypotName;
                honeypotRef.current.style.display = 'none';
            }

            // Randomize all regular field names
            Object.keys(fieldRefs.current).forEach(key => {
                const field = fieldRefs.current[key];
                if (field) {
                    const randomName = generateRandomString(10);
                    field.name = randomName;
                }
            });
        }
    }, []);

    const handleChange = (e) => {
        setFormState((prev) => ({
            ...prev,
            [e.target.id]: e.target.value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        const submissionTime = new Date().getTime() - formStartTimeRef.current;
        const honeypotValue = honeypotInputRef.current?.value;
        const isHumanCheckbox = document.getElementById('human')?.checked;

        if (submissionTime < 5000 || honeypotValue || !isHumanCheckbox) {
            setError("Your submission couldn't be processed. Please try again.");
            setIsSubmitting(false);
            return;
        }

        const part1 = 'aHR0cHM6Ly9jb250YWN0LWZvcm0tc2VydmljZS0xMjI2NzI1NTQwLnVzLWNlbnRyYWwxLnJ1bi5hcHAv';
        const part2 = 'YXBpL2NvbnRhY3Q=';
        const endpoint = atob(part1) + atob(part2);

        const h1 = atob('Q29udGVudC1UeXBl');
        const h2 = atob('T3JpZ2lu');
        const v1 = atob('YXBwbGljYXRpb24vanNvbg==');
        const v2 = atob('aHR0cHM6Ly9pZ25pdGlvbmFpLnh5eg==');

        const k1 = atob('ZW1haWw=');
        const k2 = atob('bWVzc2FnZQ==');
        const k3 = atob('c3ViamVjdA==');
        const k4 = atob('bmFtZQ==');

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    [h1]: v1,
                    [h2]: v2
                },
                body: JSON.stringify({
                    [k1]: formState.email,
                    [k2]: formState.message,
                    [k3]: `[Cathy's Sous Chef] ${formState.subject}`,
                    [k4]: formState.name
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === 'ok') {
                    setIsSubmitted(true);
                    setFormState({
                        name: "",
                        email: "",
                        subject: "",
                        message: "",
                    });
                } else {
                    setError("Submission failed. Please try again.");
                }
            } else if (response.status === 400) {
                setError("Missing required fields. Please check your email and message.");
            } else if (response.status === 403) {
                setError("Invalid request origin. Please try again.");
            } else if (response.status === 500) {
                setError("Server error. Please try again later.");
            } else {
                setError("There was a problem submitting the form. Please try again.");
            }
        } catch (err) {
            setError("Network error. Please check your connection and try again.");
        }

        setIsSubmitting(false);
    };

    return (
        <div className="min-h-screen py-12 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-black text-[var(--text-primary)] mb-6 tracking-tight">
                            Got a <span className="text-[var(--accent-color)]">Recipe</span> for Improvement?
                        </h1>
                        <p className="text-lg text-[var(--text-secondary)] mb-8 leading-relaxed max-w-2xl mx-auto font-medium">
                            Want to see a specific feature or have a question about the Sous Chef? We'd love to hear from you!
                        </p>
                    </motion.div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    {/* Contact Form */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        {isSubmitted ? (
                            <div className="glass-panel rounded-[2.5rem] p-8 sm:p-10 text-center animate-enter">
                                <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                                    <CheckCircle className="w-10 h-10 text-emerald-500" />
                                </div>
                                <h3 className="text-2xl font-black text-[var(--text-primary)] mb-4">Message Received!</h3>
                                <p className="text-[var(--text-secondary)] font-medium">
                                    Thanks for reaching out. We'll get back to you within 24 hours.
                                </p>
                            </div>
                        ) : (
                            <form
                                ref={formRef}
                                method="POST"
                                onSubmit={handleSubmit}
                                className="glass-panel rounded-[2.5rem] p-8 sm:p-10 space-y-6 relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--accent-color)]/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/2" />

                                <div ref={honeypotRef}>
                                    <label htmlFor="websiteDetails">Website Details</label>
                                    <input
                                        ref={honeypotInputRef}
                                        id="websiteDetails"
                                        type="text"
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <label htmlFor="name" className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.2em] ml-1">
                                            Name *
                                        </label>
                                        <input
                                            ref={(el) => { fieldRefs.current.name = el; }}
                                            id="name"
                                            type="text"
                                            value={formState.name}
                                            onChange={handleChange}
                                            className="input-premium w-full"
                                            placeholder="Your name"
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="email" className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.2em] ml-1">
                                            Email *
                                        </label>
                                        <input
                                            ref={(el) => { fieldRefs.current.email = el; }}
                                            id="email"
                                            type="email"
                                            value={formState.email}
                                            onChange={handleChange}
                                            className="input-premium w-full"
                                            placeholder="your@email.com"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="subject" className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.2em] ml-1">
                                        Subject *
                                    </label>
                                    <input
                                        ref={(el) => { fieldRefs.current.subject = el; }}
                                        id="subject"
                                        type="text"
                                        value={formState.subject}
                                        onChange={handleChange}
                                        className="input-premium w-full"
                                        placeholder="Feedback / Feature Request"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="message" className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.2em] ml-1">
                                        Message *
                                    </label>
                                    <textarea
                                        ref={(el) => { fieldRefs.current.message = el; }}
                                        id="message"
                                        value={formState.message}
                                        onChange={handleChange}
                                        rows={4}
                                        className="input-premium w-full resize-none"
                                        placeholder="How can we help?"
                                        required
                                    />
                                </div>

                                <div className="flex items-center justify-between pt-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="human"
                                            name="human"
                                            className="w-4 h-4 rounded border-[var(--glass-border)] bg-[var(--glass-surface)] text-[var(--accent-color)] focus:ring-[var(--accent-color)]"
                                            required
                                        />
                                        <label htmlFor="human" className="text-xs font-bold text-[var(--text-secondary)]">
                                            I am human
                                        </label>
                                    </div>
                                </div>

                                {error && (
                                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-xs font-bold text-red-500 animate-enter">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-[var(--accent-color)] text-white py-4 rounded-2xl font-black hover:brightness-110 transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Send Message</span>
                                            <Send className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </motion.div>

                    {/* Info & Social */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="space-y-8"
                    >
                        <div className="space-y-6">
                            <h2 className="text-2xl font-black text-[var(--text-primary)]">What to Expect</h2>
                            <div className="space-y-4">
                                {[
                                    { icon: Target, title: "Quick Response", desc: "We're in the kitchen daily and we read all messages." },
                                    { icon: BarChart, title: "Feature Roadmap", desc: "Your feedback directly influences our weekly development sprints." },
                                    { icon: Zap, title: "Priority Support", desc: "Premium users get direct access to our core development team." }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="p-3 bg-[var(--accent-color)]/10 rounded-2xl h-fit">
                                            <item.icon className="w-5 h-5 text-[var(--accent-color)]" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-[var(--text-primary)]">{item.title}</h4>
                                            <p className="text-sm text-[var(--text-secondary)] font-medium">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </motion.div>
                </div>
            </div>
        </div>
    );
}

function Loader2(props) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
    );
}
